import type { AutocompleteOption } from "@/components/Autocomplete";
import { withArtikelImage } from "@/components/ArtikelImage";
import { peekCurrentUser, userCanSeePrices } from "@/lib/auth";
import {
  heroArticleOrderText,
  searchHeroArticles,
  type HeroArticleSuggestion,
} from "@/lib/material";
import {
  branchSource,
  measureSource,
  projectTypeSource,
  projectStepSource,
  addressSource,
} from "@/pages/projekte/sources";
import { listKontaktKunden } from "@/lib/kontakte";
import { listHeroKunden, type HeroCustomer } from "./settingsApi";

export { branchSource, measureSource, projectTypeSource, projectStepSource, addressSource };

function heroCustomerLabel(k: HeroCustomer): string {
  const company = k.company_name || "";
  const person = `${k.first_name || ""} ${k.last_name || ""}`.trim();
  return [company, person].filter(Boolean).join(" – ") || company || person || "(ohne Namen)";
}

export function heroCustomerOption(k: HeroCustomer): AutocompleteOption<HeroCustomer> {
  return {
    key: String(k.id),
    label: heroCustomerLabel(k),
    description: k.code != null ? `Code ${k.code}` : undefined,
    value: k,
  };
}

/** App-Kunden (Geschäftspartner) für Stundensatz- und Fahrtkilometer-Zuordnung. */
export async function appCustomerSource(query: string): Promise<AutocompleteOption<HeroCustomer>[]> {
  const term = query.trim();
  if (!term) return [];
  const res = await listKontaktKunden({ q: term, limit: 50 });
  return (res.items || [])
    .map((gp) => {
      // Hero-Kontakt-ID behält die bestehende Zuordnung zu Projekten.
      // Kunden, die nur in der App existieren, werden über die Geschäftspartner-ID gespeichert.
      const id = Number(gp.hero_contact_id ?? gp.id);
      if (!Number.isFinite(id) || id <= 0) return null;
      const opt = heroCustomerOption({
        id,
        company_name: gp.firmenname,
        first_name: gp.vorname,
        last_name: gp.nachname,
      });
      return { ...opt, key: `gp-${gp.id}` };
    })
    .filter((opt): opt is AutocompleteOption<HeroCustomer> => opt != null);
}

export async function heroCustomerSource(query: string): Promise<AutocompleteOption<HeroCustomer>[]> {
  const term = query.trim();
  // Ohne Suchbegriff keine Vollabfrage (lokaler Snapshot / ggf. Hero).
  if (!term) return [];
  const list = await listHeroKunden(term);
  return (list || [])
    .slice(0, 50)
    .map((k) => {
      const id = Number(k.id);
      if (!Number.isFinite(id) || id <= 0) return null;
      return heroCustomerOption({ ...k, id });
    })
    .filter((opt): opt is AutocompleteOption<HeroCustomer> => opt != null);
}

export async function materialArticleSource(query: string): Promise<AutocompleteOption<HeroArticleSuggestion>[]> {
  const list = await searchHeroArticles(query);
  const showPrices = userCanSeePrices(peekCurrentUser());
  return list.map((a) => {
    const label = heroArticleOrderText(a);
    const parts: string[] = [];
    if (a.hero_nr) parts.push(`Artikelnummer ${a.hero_nr}`);
    if (showPrices && a.verkaufspreis_netto != null) {
      parts.push(
        `${new Intl.NumberFormat("de-DE", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(a.verkaufspreis_netto))} €`,
      );
    }
    return withArtikelImage({
      key: a.hero_nr || a.value || label,
      label,
      description: parts.length ? parts.join(" · ") : undefined,
      value: { ...a, label },
    });
  });
}

export async function printerSource(
  query: string,
  printers: string[],
): Promise<AutocompleteOption<string>[]> {
  const q = query.trim().toLowerCase();
  return printers
    .filter((p) => !q || p.toLowerCase().includes(q))
    .slice(0, 50)
    .map((p) => ({ key: p, label: p, value: p }));
}
